/**
 * Table Data Projection Helper
 * 
 * Processes table data with temporal columns (year-based) and calculates projections:
 * - Detects continuous data patterns (year columns)
 * - Calculates missing CAGR from existing values when needed
 * - Interpolates missing year values using FV (Future Value) formula
 * - Marks calculated values with filled=true for visual distinction
 */

import { logWhenInDevMode, warnWhenInDevMode } from 'utils/debug';
import { InterpolatedTableData, TableCellValue, TableData } from 'components/Reports/TableView/index';
import { applyCellTrustPolicy, getRowSourceUrl, isSourceHeader } from 'helpers/tableTrustPolicy';
import { EMPTY_CELL_VALUES, EMPTY_CELL_STRING_VALUES } from 'helpers/tablePolicy';

export { EMPTY_CELL_VALUES };

export const isMissingValue = (value: any): boolean => {
  // Unwrap TableCellValue objects from the LLM
  if (value !== null && typeof value === 'object' && 'value' in value) {
    return isMissingValue(value.value);
  }

  if (EMPTY_CELL_VALUES.includes(value)) {
    return true;
  }

  if (typeof value === 'string') {
    return EMPTY_CELL_STRING_VALUES.includes(value.toLowerCase());
  }

  return false;
};

/**
 * STEP 2: Wrap Each Cell Value
 * 
 * Take any value and wrap it in TableCellValue format:
 * { value: originalValue, filled: false }
 * 
 * filled is ALWAYS false because we never modify/estimate anything
 */
const wrapCellValue = (value: any): TableCellValue => {
  // Preserve existing cell metadata when already wrapped
  if (value !== null && typeof value === 'object' && 'value' in value) {
    return applyCellTrustPolicy({
      value: value.value,
      filled: value.filled ?? false,
      verified: value.verified,
      sourceUrl: value.sourceUrl,
      calculationDetails: value.calculationDetails,
    });
  }

  return {
    value: value,
    filled: false
  };
};

const isYearHeader = (header: string): boolean => {
  return header.toLowerCase().replace(/[^a-z]/g, '') === 'year';
};

const wrapCellWithSource = (value: any, sourceUrl?: string): TableCellValue => {
  const wrappedValue = wrapCellValue(value);

  if (sourceUrl && wrappedValue.sourceUrl === undefined) {
    return {
      ...wrappedValue,
      sourceUrl,
    };
  }

  return wrappedValue;
};

/**
 * STEP 3: Detect Year Columns
 * 
 * Detect if a column name contains a year (2020-2100).
 * Used to identify temporal/continuous data columns.
 */
const YEAR_PATTERN = /\b(20[2-9]\d|2100)\b/;

export const isYearColumn = (columnName: string): boolean => {
  return YEAR_PATTERN.test(columnName);
};

export const extractYearFromColumn = (columnName: string): number | null => {
  const matches = columnName.match(/\b(20[2-9]\d|2100)\b/g);

  if (!matches || matches.length === 0) {
    return null;
  }

  // Use the last (most distant) year
  const years = matches.map(y => parseInt(y, 10));
  return Math.max(...years);
};
/**
 * STEP 4: Detect Continuous Data Pattern
 * 
 * Check if table headers contain continuous/temporal data pattern.
 * A pattern exists when there are at least 2 year columns.
 * This determines if we should apply interpolation logic or not.
 */
const MIN_YEAR_COLUMNS = 2;

export const hasContinuousDataPattern = (headers: string[]): boolean => {
  const yearColumns = headers.filter(isYearColumn);
  return yearColumns.length >= MIN_YEAR_COLUMNS;
};

/**
 * STEP 5: FV (Future Value) Calculation (Private Helper)
 * 
 * Calculate future value using Excel's FV formula:
 * FV(rate, nper, pmt, pv)
 * 
 * In our case: FV(CAGR, yearDiff, 0, -presentValue)
 * Result is rounded to 2 decimal places
 */
const calculateFutureValue = (rate: number, nper: number, pv: number): number => {
  if (rate === 0) {
    return pv;
  }

  // FV = PV * (1 + rate)^nper
  const fv = pv * Math.pow(1 + rate, nper);
  return Math.round(fv * 100) / 100;
};

/**
 * STEP 6: Find Missing Year Values in Row (Private Helper)
 * 
 * Check which year columns have missing values in a specific row.
 * Returns array of column names that are missing.
 */
const findMissingYearValues = (row: any, yearColumns: string[]): string[] => {
  return yearColumns.filter(column => {
    const value = row[column];
    if (isMissingValue(value)) return true;
    // Year cells must be numeric. Any non-parseable content (placeholder
    // text, parentheticals, dashes, etc.) is treated as missing so the
    // projector fills it from CAGR + an anchor.
    return extractNumericValue(value) === null;
  });
};

/**
 * STEP 7: Extract Numeric Value (Private Helper)
 * 
 * Extract numeric value from various formats:
 * - "1.1 (2023)" → 1.1
 * - "30.6" → 30.6
 * - "2,529.10" → 2529.10
 * - "1,234,567.89" → 1234567.89
 * - 30.6 → 30.6
 * - "N/A" → null
 */
const NUMERIC_PATTERN = /([\d,]+\.?\d*)/;

const extractNumericValue = (value: any): number | null => {
  // Unwrap TableCellValue objects from the LLM
  if (value !== null && typeof value === 'object' && 'value' in value) {
    return extractNumericValue(value.value);
  }

  if (isMissingValue(value)) {
    return null;
  }

  // If it's already a number, return it
  if (typeof value === 'number') {
    return value;
  }

  // If it's a string, extract the first number (with commas)
  if (typeof value === 'string') {
    const match = value.match(NUMERIC_PATTERN);
    if (match) {
      const cleanedNumber = match[1].replace(/,/g, '');
      return parseFloat(cleanedNumber);
    }
  }

  return null;
};

const MISSING_VALUE_PREFIX_PATTERN = /^(na|n\/a|not specified|not available|not stated)\b/i;

const normalizeYearColumnValue = (value: any): any => {
  if (value !== null && typeof value === 'object' && 'value' in value) {
    return {
      ...value,
      value: normalizeYearColumnValue(value.value)
    };
  }

  if (typeof value !== 'string') {
    return value;
  }

  const trimmedValue = value.trim();

  // Keep missing values blank when extra context is appended (e.g., "N/A (2024 base)").
  if (MISSING_VALUE_PREFIX_PATTERN.test(trimmedValue)) {
    return '';
  }

  // Year columns should be numeric-only; drop parenthetical/context text if present.
  if (/[a-zA-Z()]/.test(trimmedValue)) {
    const numericValue = extractNumericValue(trimmedValue);
    return numericValue !== null ? numericValue : value;
  }

  return value;
};

const NUMERIC_QUALIFIER_PATTERN = /^[\s~<>≈]+/;
const NUMERIC_LIKE_PATTERN = /^[\s~<>≈]*\d[\d,]*(\.\d+)?\s*$/;

const normalizeNumericLikeValue = (value: any): any => {
  if (value !== null && typeof value === 'object' && 'value' in value) {
    return {
      ...value,
      value: normalizeNumericLikeValue(value.value)
    };
  }

  if (typeof value !== 'string') {
    return value;
  }

  const trimmedValue = value.trim();

  // Unlike normalizeYearColumnValue, non-year columns (e.g. CAGR) keep an
  // explicit "N/A" from the source as-is rather than blanking it, so the
  // table still shows why a value is missing instead of an empty cell.
  if (!NUMERIC_LIKE_PATTERN.test(trimmedValue)) {
    return value;
  }

  const numericCandidate = trimmedValue.replace(NUMERIC_QUALIFIER_PATTERN, '');
  const numericValue = extractNumericValue(numericCandidate);
  return numericValue !== null ? numericValue : value;
};

/**
 * STEP 8: Extract CAGR Value (Private Helper)
 * 
 * Extract numeric CAGR rate from various formats:
 * - "38.5% (24-30)" → 0.385
 * - "56.75" → 0.5675
 * - 56.75 → 0.5675
 * - "N/A" → null
 */
const CAGR_PATTERN = /(\d+\.?\d*)\s*%?/;

const extractCAGRValue = (cagrValue: any): number | null => {
  // Unwrap TableCellValue objects from the LLM
  if (cagrValue !== null && typeof cagrValue === 'object' && 'value' in cagrValue) {
    return extractCAGRValue(cagrValue.value);
  }

  if (isMissingValue(cagrValue)) {
    return null;
  }

  // If it's a number, convert to decimal
  if (typeof cagrValue === 'number') {
    return cagrValue / 100;
  }

  // If it's a string, extract the number
  const cagrString = String(cagrValue);

  const match = cagrString.match(CAGR_PATTERN);
  if (!match) {
    return null;
  }

  const numericValue = parseFloat(match[1]);
  return numericValue / 100;
};

/**
 * STEP 7.5: Calculate CAGR from Values (Private Helper)
 * 
 * When CAGR is missing, calculate it from two known values
 * Formula: CAGR = (FV/PV)^(1/nper) - 1
 */
const calculateCAGRFromValues = (
  row: any,
  yearColumns: string[]
): number | null => {
  // Find two non-missing values with the largest time gap
  const knownValues: Array<{ column: string, year: number, value: number }> = [];

  yearColumns.forEach(col => {
    const value = extractNumericValue(row[col]);
    const year = extractYearFromColumn(col);

    if (value !== null && year !== null && value > 0) {
      knownValues.push({ column: col, year, value });
    }
  });

  // Need at least 2 values to calculate CAGR
  if (knownValues.length < 2) {
    return null;
  }

  // Sort by year and get the first and last values (largest time span)
  knownValues.sort((a, b) => a.year - b.year);
  const firstValue = knownValues[0];
  const lastValue = knownValues[knownValues.length - 1];

  const nper = lastValue.year - firstValue.year;

  if (nper <= 0) {
    return null;
  }

  // CAGR = (FV/PV)^(1/nper) - 1
  const cagr = Math.pow(lastValue.value / firstValue.value, 1 / nper) - 1;

  return cagr;
};

/**
 * Type definition for known year data
 */
type KnownYearData = {
  column: string;
  year: number;
  value: number;
};

/**
 * Extract known year data from row (Pure Function)
 * Maps year columns to their year and value data
 */
const extractKnownYearData = (
  row: any,
  yearColumns: string[],
  missingColumns: string[]
): KnownYearData[] => {
  return yearColumns
    .filter(c => !missingColumns.includes(c))
    .map(c => ({
      column: c,
      year: extractYearFromColumn(c),
      value: extractNumericValue(row[c])
    }))
    .filter((item): item is KnownYearData =>
      item.year !== null && item.value !== null
    )
    .sort((a, b) => a.year - b.year);
};

/**
 * Find closest base year for projection (Pure Function)
 * Returns the closest previous year, or first year if no previous year exists
 */
const findClosestBaseYear = (
  targetYear: number,
  knownYearData: KnownYearData[]
): KnownYearData | null => {
  if (knownYearData.length === 0) {
    return null;
  }

  // Try to find the closest previous year (for forward projection)
  const previousYears = knownYearData
    .filter(item => item.year <= targetYear)
    .sort((a, b) => b.year - a.year); // Sort descending (closest first)

  if (previousYears.length > 0) {
    return previousYears[0];
  }

  // No previous year, use the first known year for backward projection
  return knownYearData[0];
};

/**
 * Calculate projected value for a single column (Pure Function)
 */
const calculateProjectedValue = (
  column: string,
  cagrRate: number,
  knownYearData: KnownYearData[]
): TableCellValue | null => {
  const targetYear = extractYearFromColumn(column);

  if (!targetYear) {
    return null;
  }

  const baseItem = findClosestBaseYear(targetYear, knownYearData);

  if (!baseItem) {
    return null;
  }

  const yearDiff = targetYear - baseItem.year;
  const calculatedValue = calculateFutureValue(cagrRate, yearDiff, baseItem.value);
  const cagrPercent = (cagrRate * 100).toFixed(1);
  const calculationDetails = `CAGR projection: $${baseItem.value}B × (1 + ${cagrPercent}%)^${yearDiff} yr = $${calculatedValue}B`;

  return {
    value: calculatedValue,
    filled: true,
    calculationDetails
  };
};

/**
 * Wrap year columns with values (Pure Function)
 */
const wrapYearColumns = (
  row: any,
  yearColumns: string[],
  missingColumns: string[],
  cagrRate: number | null,
  knownYearData: KnownYearData[]
): { [key: string]: TableCellValue } => {
  const values: { [key: string]: TableCellValue } = {};

  yearColumns.forEach(col => {
    if (missingColumns.includes(col) && cagrRate && knownYearData.length > 0) {
      const projectedValue = calculateProjectedValue(col, cagrRate, knownYearData);
      values[col] = projectedValue || wrapCellValue(row[col]);
    } else {
      values[col] = wrapCellValue(row[col]);
    }
  });

  return values;
};

/**
 * STEP 8: Interpolate Row Values (Orchestrator Function)
 * 
 * For a single row, calculate missing year values using FV formula.
 * Returns object with wrapped cell values (TableCellValue format).
 */
const interpolateRowValues = (
  row: any,
  yearColumns: string[],
  cagrColumn: string
): { values: { [key: string]: TableCellValue }, cagrWasCalculated: boolean, calculatedCAGR: number | null } => {
  // Extract CAGR rate
  let cagrRate = extractCAGRValue(row[cagrColumn]);
  let cagrWasCalculated = false;
  let calculatedCAGR: number | null = null;

  // If CAGR is missing, try to calculate it from values
  if (!cagrRate) {
    cagrRate = calculateCAGRFromValues(row, yearColumns);
    if (cagrRate) {
      cagrWasCalculated = true;
      calculatedCAGR = cagrRate;
    }
  }

  // Find missing columns
  const missingColumns = findMissingYearValues(row, yearColumns);

  // If no CAGR or no missing values, just wrap and return
  if (!cagrRate || missingColumns.length === 0) {
    const values = wrapYearColumns(row, yearColumns, [], null, []);
    return { values, cagrWasCalculated, calculatedCAGR };
  }

  // Extract known year data
  const knownYearData = extractKnownYearData(row, yearColumns, missingColumns);

  // Wrap year columns with projections
  const values = wrapYearColumns(row, yearColumns, missingColumns, cagrRate, knownYearData);

  return { values, cagrWasCalculated, calculatedCAGR };
};

/**
 * STEP 9: Process Table with Pattern Detection
 * 
 * Smart processing:
 * - If no continuous pattern detected → return data directly (no calculations)
 * - If continuous pattern detected → apply FV calculations for missing values
 */
export const processTableWithPatternDetection = (tableData: TableData): InterpolatedTableData => {
  const { headers, rows, title } = tableData;
  const hasContinuousPattern = hasContinuousDataPattern(headers);

  if (!hasContinuousPattern) {
    // No pattern = return directly without any calculations
    return simpleTableData(tableData);
  }

  // Find year columns and CAGR column
  const yearColumns = headers.filter(isYearColumn);
  const cagrColumn = headers.find(h =>
    h.toLowerCase().includes('cagr') ||
    h.toLowerCase().includes('growth rate')
  );

  if (!cagrColumn) {
    warnWhenInDevMode('[Table] "%s" — no CAGR column found, skipping projections.', title);
    return simpleTableData(tableData);
  }

  let hasAnyFilledValue = false;
  const projectionRows: { row: number; cagrSource: string; projected: { col: string; value: unknown; detail: string | undefined; sourceUrl: string | undefined }[] }[] = [];

  // Process each row
  const processedRows = rows.map((row, rowIndex) => {
    const processedRow: { [key: string]: TableCellValue } = {};
    const rowSourceUrl = getRowSourceUrl(row, headers);

    // Interpolate year columns
    const interpolationResult = interpolateRowValues(row, yearColumns, cagrColumn);

    // Collect projection details for this row
    const filledCols = Object.entries(interpolationResult.values)
      .filter(([, cell]) => cell.filled)
      .map(([col, cell]) => ({ col, value: cell.value, detail: cell.calculationDetails, sourceUrl: cell.sourceUrl }));

    if (filledCols.length > 0) {
      projectionRows.push({
        row: rowIndex,
        cagrSource: interpolationResult.cagrWasCalculated
          ? `derived (${(interpolationResult.calculatedCAGR! * 100).toFixed(2)}%)`
          : 'provided',
        projected: filledCols,
      });
    }

    // Check if any value was filled in this row
    Object.values(interpolationResult.values).forEach(cell => {
      if (cell.filled) {
        hasAnyFilledValue = true;
      }
    });

    // Add interpolated year values with source URL fallback
    Object.entries(interpolationResult.values).forEach(([col, cell]) => {
      processedRow[col] = {
        ...cell,
        sourceUrl: cell.sourceUrl || rowSourceUrl
      };
    });

    // Wrap non-year columns (including CAGR)
    headers.forEach(header => {
      if (!yearColumns.includes(header)) {
        // If this is the CAGR column and it was calculated, mark it as filled
        if (header === cagrColumn && interpolationResult.cagrWasCalculated && interpolationResult.calculatedCAGR !== null) {
          processedRow[header] = {
            value: `${(interpolationResult.calculatedCAGR * 100).toFixed(2)}%*`,
            filled: true
          };
        } else {
          const normalizedValue = normalizeNumericLikeValue(row[header]);
          processedRow[header] = wrapCellWithSource(normalizedValue, isSourceHeader(header) ? undefined : rowSourceUrl);
        }
      }
    });

    return processedRow;
  });

  const filledRowCount = processedRows.filter(r => Object.values(r).some(c => c.filled)).length;
  logWhenInDevMode('[Table] "%s" — projection: cagrCol "%s", %d/%d rows projected%s',
    title, cagrColumn, filledRowCount, rows.length,
    projectionRows.length > 0 ? ' | rows: ' + JSON.stringify(projectionRows) : ''
  );

  return {
    data: {
      title,
      headers,
      rows: processedRows
    },
    isFilled: hasAnyFilledValue
  };
};

/**
 * STEP 10: Process Table Data (Simple Wrapper)
 * 
 * Take the input table and wrap all values.
 * No modifications, no estimations, no interpolation.
 */
export const simpleTableData = (tableData: TableData): InterpolatedTableData => {
  const hasYearAxisColumn = tableData.headers.some(isYearHeader);
  const hasHeaderYearColumns = tableData.headers.some(isYearColumn);
  const shouldNormalizeRowYearTable = hasYearAxisColumn && !hasHeaderYearColumns;

  // Process each row
  const processedRows = tableData.rows.map(row => {
    const processedRow: { [key: string]: TableCellValue } = {};
    const rowSourceUrl = getRowSourceUrl(row, tableData.headers);

    // Wrap each cell value
    tableData.headers.forEach(header => {
      const originalValue = row[header];
      const normalizedValue = shouldNormalizeRowYearTable
        ? (isYearHeader(header)
          ? normalizeYearColumnValue(originalValue)
          : normalizeNumericLikeValue(originalValue))
        : originalValue;

      processedRow[header] = wrapCellWithSource(normalizedValue, isSourceHeader(header) ? undefined : rowSourceUrl);
    });

    return processedRow;
  });

  return {
    data: {
      title: tableData.title,
      headers: tableData.headers,
      rows: processedRows
    },
    isFilled: false // We never fill anything
  };
};

// Export as default
export default {
  processTableWithPatternDetection,
  simpleTableData,
  isMissingValue,
  isYearColumn,
  extractYearFromColumn,
  hasContinuousDataPattern
};

// Export pure helper functions for testing
export {
  extractKnownYearData,
  findClosestBaseYear,
  calculateProjectedValue,
  wrapYearColumns,
  type KnownYearData
};
